import { z } from 'zod';
import { editionStatusSchema } from './editions.js';
import { activityStatuses, audienceSchema, statsCategories } from './program.js';

/**
 * PRD 17 US-801: statystyki edycji w trakcie zapisów (definicje: PRD 17 §2). Pojemność = limit + rezerwa; do sum miejsc
 * wchodzą tylko aktywne zajęcia z zapisami. Obłożenie jako ułamek 0…1 (interfejs formatuje po polsku – AC-7).
 */

/** Grupy wiekowe bez nakładania się granic (Z-803). */
export const ageGroups = ['3-5', '6-7', '8-11', '12-14', '15-18', 'adults', 'adults_only'] as const;
export type AgeGroup = (typeof ageGroups)[number];
export const AGE_GROUP_RANGES: Record<Exclude<AgeGroup, 'adults' | 'adults_only'>, [number, number]> = {
  '3-5': [3, 5],
  '6-7': [6, 7],
  '8-11': [8, 11],
  '12-14': [12, 14],
  '15-18': [15, 18],
};

/**
 * Zakres raportu: jedna edycja albo wszystkie edycje jednego dnia (PRD §10 pkt 57) – wtedy rodzina zapisana w dwóch
 * edycjach liczy się raz.
 */
export const statsEditionSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  city: z.string(),
  placeName: z.string().nullable(),
  date: z.string(),
});
export type StatsEdition = z.infer<typeof statsEditionSchema>;

/** Edycje do wyboru na stronie statystyk (także szkice i zakończone), od najnowszej. */
export const statsEditionListSchema = z.object({
  editions: z.array(statsEditionSchema.extend({ status: editionStatusSchema })),
});
export type StatsEditionList = z.infer<typeof statsEditionListSchema>;

const seatsSchema = z.object({
  capacity: z.number().int(),
  reserve: z.number().int(),
  enrolled: z.number().int(),
  freeInLimit: z.number().int(),
  freeInReserve: z.number().int(),
  occupancy: z.number(),
});

export const statsActivitySchema = z.object({
  id: z.string().uuid(),
  editionId: z.string().uuid(),
  symbol: z.string(),
  title: z.string(),
  type: z.string(),
  category: z.enum(statsCategories),
  room: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  audience: audienceSchema,
  ageMin: z.number().int().nullable(),
  ageMax: z.number().int().nullable(),
  status: z.enum(activityStatuses),
  enrollment: z.boolean(),
  enrolled: z.number().int(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  /** zapisani / (limit + rezerwa); null – bez zapisów albo odwołane. */
  occupancy: z.number().nullable(),
});
export type StatsActivity = z.infer<typeof statsActivitySchema>;

const timelinePointSchema = z.object({ label: z.string(), registrations: z.number().int(), enrollments: z.number().int() });
export type TimelinePoint = z.infer<typeof timelinePointSchema>;

export const editionStatsSchema = z.object({
  date: z.string(),
  editions: z.array(statsEditionSchema).min(1),
  generatedAt: z.string(),
  accounts: z.object({
    registered: z.number().int(),
    earlyAccess: z.number().int(),
    /** Tylko dla całego dnia: konta z zapisami + konta wszystkich prowadzących (zakładka „Stare”, PRD §10 pkt 60). */
    withSpeakers: z.number().int().optional(),
  }),
  people: z.object({ children: z.number().int(), adults: z.number().int(), childrenWithoutActivities: z.number().int() }),
  activities: z.object({
    workshop: z.number().int(),
    talk: z.number().int(),
    other: z.number().int(),
    cancelled: z.number().int(),
  }),
  seats: seatsSchema,
  emptyCount: z.number().int(),
  fullCount: z.number().int(),
  ageGroups: z.array(z.object({ group: z.enum(ageGroups), seats: z.number().int(), enrolled: z.number().int(), occupancy: z.number() })),
  table: z.array(statsActivitySchema),
  /** AC-6: dzień po dniu (etykieta `YYYY-MM-DD`) i dzień otwarcia zapisów godzina po godzinie (`HH:00`). */
  timeline: z.object({ daily: z.array(timelinePointSchema), openingDay: z.string(), hourly: z.array(timelinePointSchema) }),
});
export type EditionStats = z.infer<typeof editionStatsSchema>;

/**
 * PRD 17 US-802: statystyki obecności. Odprawa według bieżącego stanu (cofnięcie liczy się od razu – US-604 AC-2),
 * odprawy w przedziałach po 15 min; obecność na zajęciach z oznaczeniem list niepotwierdzonych (AC-2).
 */
const checkInCountSchema = z.object({ checkedIn: z.number().int(), registered: z.number().int() });

export const attendanceStatsSchema = z.object({
  date: z.string(),
  editions: z.array(statsEditionSchema).min(1),
  generatedAt: z.string(),
  checkIn: z.object({ accounts: checkInCountSchema, children: checkInCountSchema, adults: checkInCountSchema }),
  /** AC-1: odprawy w czasie – etykieta `YYYY-MM-DD HH:MM` (początek 15-minutowego przedziału, czas wydarzenia). */
  checkInTimeline: z.array(z.object({ label: z.string(), count: z.number().int() })),
  /** AC-2: zajęcia z zapisami (bez odwołanych). */
  activities: z.array(
    z.object({
      id: z.string().uuid(),
      editionId: z.string().uuid(),
      symbol: z.string(),
      title: z.string(),
      startsAt: z.string(),
      enrolled: z.number().int(),
      attended: z.number().int(),
      confirmed: z.boolean(),
    }),
  ),
  /** Suma dla zajęć z potwierdzoną listą. */
  totals: z.object({ attended: z.number().int(), enrolled: z.number().int(), unconfirmed: z.number().int() }),
  /** AC-3: osoby zapisane na zajęcia, ale nieodprawione na wejściu. */
  enrolledNotCheckedIn: z.number().int(),
});
export type AttendanceStats = z.infer<typeof attendanceStatsSchema>;
